import React, { useEffect, useMemo, useState } from 'react';
import { apiClient } from '../utils/apiClient';
import { Link } from 'react-router-dom';
import { API_BASE_URL } from '../constants/api';
import { useAuth } from '../context/AuthContext';
import { useUISettings } from '../context/UISettingsContext';
import AppIcon from '../components/AppIcon';
import '../styles/Dashboard.css';

const DASHBOARD_EMOJI_ICONS = {
  construction: '🧱',
  active: '🚧',
  completed: '✅',
  tasks: '📋',
  projects: '📁',
  plus: '➕',
};

const STATUS_LABELS = {
  planning: 'Planlama',
  in_progress: 'Devam Ediyor',
  completed: 'Tamamlandı',
  on_hold: 'Beklemede',
};

const flattenBoardOptions = (nodes, level = 0, collector = []) => {
  nodes.forEach((node) => {
    collector.push({
      id: Number(node.id),
      name: `${'  '.repeat(level)}${node.name}`,
      level,
    });
    flattenBoardOptions(node.children || [], level + 1, collector);
  });
  return collector;
};

const Dashboard = () => {
  const { user } = useAuth();
  const { iconMode } = useUISettings();
  const [stats, setStats] = useState({
    totalProjects: 0,
    activeProjects: 0,
    completedProjects: 0,
    activeTasks: 0,
    pendingPuantaj: 0,
  });
  const [recentProjects, setRecentProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState('');
  const [aiResult, setAiResult] = useState(null);
  const [aiHistory, setAiHistory] = useState([]);
  const [aiHealthLoading, setAiHealthLoading] = useState(false);
  const [aiHealthError, setAiHealthError] = useState('');
  const [aiHealthOverview, setAiHealthOverview] = useState({
    summary: {
      project_count: 0,
      critical_projects: 0,
      watch_projects: 0,
      healthy_projects: 0,
      portfolio_health_score: 0,
    },
    cost_context: null,
    rows: [],
  });
  const [riskForecastLoading, setRiskForecastLoading] = useState(false);
  const [riskForecastError, setRiskForecastError] = useState('');
  const [riskForecastData, setRiskForecastData] = useState({
    summary: {
      tracked_projects: 0,
      critical_projects: 0,
      high_risk_projects: 0,
      average_delay_probability_pct: 0,
    },
    rows: [],
  });
  const [applyingProjectId, setApplyingProjectId] = useState(null);
  const [applyingActionProjectId, setApplyingActionProjectId] = useState(null);
  const [actionModalOpen, setActionModalOpen] = useState(false);
  const [actionModalProject, setActionModalProject] = useState(null);
  const [actionModalUsers, setActionModalUsers] = useState([]);
  const [actionModalBoards, setActionModalBoards] = useState([]);
  const [actionModalLoading, setActionModalLoading] = useState(false);
  const [actionModalError, setActionModalError] = useState('');
  const [actionForm, setActionForm] = useState({
    assigned_to: '',
    board_node_id: '',
    start_date: new Date().toISOString().slice(0, 10),
  });
  const [metricsRangeDays, setMetricsRangeDays] = useState(7);
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [metricsExporting, setMetricsExporting] = useState(false);
  const [capacityLoading, setCapacityLoading] = useState(false);
  const [capacityError, setCapacityError] = useState('');
  const [capacityWeekDays, setCapacityWeekDays] = useState(7);
  const [capacityOverview, setCapacityOverview] = useState({
    summary: {
      total_people: 0,
      overloaded_people: 0,
      critical_people: 0,
      average_utilization_pct: 0,
    },
    rows: [],
    alerts: [],
    rebalancing: {
      summary: {
        impacted_sources: 0,
        impacted_targets: 0,
        total_suggestions: 0,
      },
      suggestions: [],
    },
    scope: { week_days: 7 },
  });
  const [earlyWarningLoading, setEarlyWarningLoading] = useState(false);
  const [earlyWarningError, setEarlyWarningError] = useState('');
  const [earlyWarningData, setEarlyWarningData] = useState({
    summary: {
      tracked_projects: 0,
      critical_projects: 0,
      high_projects: 0,
      medium_projects: 0,
      overdue_task_total: 0,
      pending_puantaj_total: 0,
    },
    rows: [],
    alerts: [],
  });
  const [notificationMetrics, setNotificationMetrics] = useState({
    scope: 'user',
    range_days: 7,
    summary: { created_count: 0, read_count: 0, unread_count: 0 },
    type_breakdown: [],
    delivery_summary: [],
    recent_failures: [],
  });

  const API_BASE = API_BASE_URL;
  const canUseAiScan = [1, 2].includes(Number(user?.role_id));
  const canViewCapacity = [1, 2, 3].includes(Number(user?.role_id));
  const canViewEarlyWarning = [1, 2, 3].includes(Number(user?.role_id));

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    fetchDashboardData();
    fetchNotificationMetrics(metricsRangeDays);
    if (canViewEarlyWarning) {
      fetchEarlyWarningCenter();
    }
    if (canViewCapacity) {
      fetchCapacityOverview(capacityWeekDays);
    }
    if (canUseAiScan) {
      fetchAiHealthOverview();
      fetchRiskForecast();
      fetchAiHistory();
    }
  }, [canUseAiScan, canViewCapacity, canViewEarlyWarning, metricsRangeDays, capacityWeekDays]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [projectsRes, tasksRes] = await Promise.all([
        apiClient.get(`${API_BASE}/projects`),
        apiClient.get(`${API_BASE}/tasks`, {
          validateStatus: () => true,
        }),
      ]);

      const projects = Array.isArray(projectsRes.data) ? projectsRes.data : [];
      const tasks = Array.isArray(tasksRes.data) ? tasksRes.data : [];
      const activeProjects = projects.filter((p) => p.status === 'in_progress').length;
      const completedProjects = projects.filter((p) => p.status === 'completed').length;
      const activeTasks = tasks.filter((t) => ['in_progress', 'to_do', 'blocked'].includes(String(t.status || '').toLowerCase())).length;

      setStats({
        totalProjects: projects.length,
        activeProjects,
        completedProjects,
        activeTasks,
        pendingPuantaj: 0,
      });
      setRecentProjects(projects.slice(0, 5));
      setError(null);
    } catch (fetchError) {
      setError('Pano verileri yüklenirken hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const runAiScan = async (projectId = null) => {
    try {
      setAiLoading(true);
      setAiError('');
      const payload = projectId ? { project_id: Number(projectId) } : {};
      const response = await apiClient.post(`${API_BASE}/ai/project-scan`, payload);
      setAiResult(response.data || null);
      await fetchAiHistory();
    } catch (scanError) {
      setAiError(scanError.response?.data?.message || 'AI analiz çalıştırılamadı.');
      setAiResult(null);
    } finally {
      setAiLoading(false);
    }
  };

  const fetchAiHistory = async () => {
    try {
      const response = await apiClient.get(`${API_BASE}/ai/project-scan/history?limit=8`);
      setAiHistory(Array.isArray(response.data?.runs) ? response.data.runs : []);
    } catch (historyError) {
      setAiHistory([]);
    }
  };

  const fetchAiHealthOverview = async () => {
    try {
      setAiHealthLoading(true);
      setAiHealthError('');
      const response = await apiClient.get(`${API_BASE}/ai/project-health-overview?limit=6`);
      setAiHealthOverview({
        summary: {
          project_count: Number(response.data?.summary?.project_count || 0),
          critical_projects: Number(response.data?.summary?.critical_projects || 0),
          watch_projects: Number(response.data?.summary?.watch_projects || 0),
          healthy_projects: Number(response.data?.summary?.healthy_projects || 0),
          portfolio_health_score: Number(response.data?.summary?.portfolio_health_score || 0),
        },
        cost_context: response.data?.cost_context || null,
        rows: Array.isArray(response.data?.rows) ? response.data.rows : [],
      });
    } catch (healthError) {
      setAiHealthOverview({
        summary: {
          project_count: 0,
          critical_projects: 0,
          watch_projects: 0,
          healthy_projects: 0,
          portfolio_health_score: 0,
        },
        cost_context: null,
        rows: [],
      });
      setAiHealthError(healthError.response?.data?.message || 'AI sağlık skoru verileri alınamadı.');
    } finally {
      setAiHealthLoading(false);
    }
  };

  const fetchRiskForecast = async () => {
    try {
      setRiskForecastLoading(true);
      setRiskForecastError('');
      const response = await apiClient.get(`${API_BASE}/ai/risk-forecast?days=7&limit=6`);
      setRiskForecastData({
        summary: {
          tracked_projects: Number(response.data?.summary?.tracked_projects || 0),
          critical_projects: Number(response.data?.summary?.critical_projects || 0),
          high_risk_projects: Number(response.data?.summary?.high_risk_projects || 0),
          average_delay_probability_pct: Number(response.data?.summary?.average_delay_probability_pct || 0),
        },
        rows: Array.isArray(response.data?.rows) ? response.data.rows : [],
      });
    } catch (requestError) {
      setRiskForecastData({
        summary: {
          tracked_projects: 0,
          critical_projects: 0,
          high_risk_projects: 0,
          average_delay_probability_pct: 0,
        },
        rows: [],
      });
      setRiskForecastError(requestError.response?.data?.message || 'Risk tahmin verileri alınamadı.');
    } finally {
      setRiskForecastLoading(false);
    }
  };

  const fetchNotificationMetrics = async (rangeDays = metricsRangeDays) => {
    try {
      setMetricsLoading(true);
      const response = await apiClient.get(`${API_BASE}/notifications/metrics?range_days=${Number(rangeDays) || 7}`);
      const data = response.data || {};
      setNotificationMetrics({
        scope: data.scope || 'user',
        range_days: Number(data.range_days || 7),
        summary: {
          created_count: Number(data.summary?.created_count || 0),
          read_count: Number(data.summary?.read_count || 0),
          unread_count: Number(data.summary?.unread_count || 0),
        },
        type_breakdown: Array.isArray(data.type_breakdown) ? data.type_breakdown : [],
        delivery_summary: Array.isArray(data.delivery_summary) ? data.delivery_summary : [],
        recent_failures: Array.isArray(data.recent_failures) ? data.recent_failures : [],
      });
    } catch (metricsError) {
      setNotificationMetrics({
        scope: 'user',
        range_days: Number(rangeDays) || 7,
        summary: { created_count: 0, read_count: 0, unread_count: 0 },
        type_breakdown: [],
        delivery_summary: [],
        recent_failures: [],
      });
    } finally {
      setMetricsLoading(false);
    }
  };

  const fetchCapacityOverview = async (weekDays = capacityWeekDays) => {
    try {
      setCapacityLoading(true);
      setCapacityError('');
      const response = await apiClient.get(
        `${API_BASE}/tasks/capacity/overview?week_days=${Number(weekDays) || 7}`,
        
      );
      const data = response.data || {};
      setCapacityOverview({
        summary: {
          total_people: Number(data.summary?.total_people || 0),
          overloaded_people: Number(data.summary?.overloaded_people || 0),
          critical_people: Number(data.summary?.critical_people || 0),
          average_utilization_pct: Number(data.summary?.average_utilization_pct || 0),
        },
        rows: Array.isArray(data.rows) ? data.rows : [],
        alerts: Array.isArray(data.alerts) ? data.alerts : [],
        rebalancing: {
          summary: {
            impacted_sources: Number(data.rebalancing?.summary?.impacted_sources || 0),
            impacted_targets: Number(data.rebalancing?.summary?.impacted_targets || 0),
            total_suggestions: Number(data.rebalancing?.summary?.total_suggestions || 0),
          },
          suggestions: Array.isArray(data.rebalancing?.suggestions) ? data.rebalancing.suggestions : [],
        },
        scope: {
          week_days: Number(data.scope?.week_days || Number(weekDays) || 7),
        },
      });
    } catch (requestError) {
      setCapacityOverview({
        summary: {
          total_people: 0,
          overloaded_people: 0,
          critical_people: 0,
          average_utilization_pct: 0,
        },
        rows: [],
        alerts: [],
        rebalancing: {
          summary: {
            impacted_sources: 0,
            impacted_targets: 0,
            total_suggestions: 0,
          },
          suggestions: [],
        },
        scope: { week_days: Number(weekDays) || 7 },
      });
      setCapacityError(requestError.response?.data?.message || 'Kapasite verileri yüklenemedi.');
    } finally {
      setCapacityLoading(false);
    }
  };

  const fetchEarlyWarningCenter = async () => {
    try {
      setEarlyWarningLoading(true);
      setEarlyWarningError('');
      const response = await apiClient.get(`${API_BASE}/projects/early-warning?limit=8`);
      const data = response.data || {};
      setEarlyWarningData({
        summary: {
          tracked_projects: Number(data.summary?.tracked_projects || 0),
          critical_projects: Number(data.summary?.critical_projects || 0),
          high_projects: Number(data.summary?.high_projects || 0),
          medium_projects: Number(data.summary?.medium_projects || 0),
          overdue_task_total: Number(data.summary?.overdue_task_total || 0),
          pending_puantaj_total: Number(data.summary?.pending_puantaj_total || 0),
        },
        rows: Array.isArray(data.rows) ? data.rows : [],
        alerts: Array.isArray(data.alerts) ? data.alerts : [],
      });
    } catch (requestError) {
      setEarlyWarningData({
        summary: {
          tracked_projects: 0,
          critical_projects: 0,
          high_projects: 0,
          medium_projects: 0,
          overdue_task_total: 0,
          pending_puantaj_total: 0,
        },
        rows: [],
        alerts: [],
      });
      setEarlyWarningError(requestError.response?.data?.message || 'Erken uyarı merkezi verileri yüklenemedi.');
    } finally {
      setEarlyWarningLoading(false);
    }
  };

  const exportNotificationMetricsCsv = async () => {
    try {
      setMetricsExporting(true);
      const response = await apiClient.get(
        `${API_BASE}/notifications/metrics/export?range_days=${Number(metricsRangeDays) || 7}`,
        { responseType: 'blob' }
      );

      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' });
      const fileName = `notification-metrics-${notificationMetrics.scope || 'user'}-${Number(metricsRangeDays) || 7}d.csv`;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (downloadError) {
      setError('Bildirim metrik CSV dosyası oluşturulurken hata oluştu.');
    } finally {
      setMetricsExporting(false);
    }
  };

  const applyAiChecklist = async (projectId) => {
    const runId = Number(aiResult?.run_id || aiHistory[0]?.id);
    if (!runId) {
      setAiError('Checklist aktarımı için önce AI analiz çalıştırın.');
      return;
    }

    try {
      setApplyingProjectId(Number(projectId));
      setAiError('');
      const response = await apiClient.post(
        `${API_BASE}/ai/project-scan/apply-checklist`,
        {
          run_id: runId,
          project_id: Number(projectId),
        }
      );
      window.alert(`Checklist oluşturuldu. Liste ID: ${response.data?.checklist_id}`);
    } catch (applyError) {
      setAiError(applyError.response?.data?.message || 'Checklist aktarımı başarısız.');
    } finally {
      setApplyingProjectId(null);
    }
  };

  const applyAiActions = async (projectId) => {
    const targetProjectId = Number(projectId || actionModalProject?.project_id || 0);
    const runId = Number(aiResult?.run_id || aiHistory[0]?.id);
    if (!runId) {
      setAiError('AI aksiyonlarını göreve çevirmek için önce analiz çalıştırın.');
      return;
    }

    try {
      setApplyingActionProjectId(targetProjectId);
      setAiError('');
      setActionModalError('');
      const response = await apiClient.post(
        `${API_BASE}/ai/project-scan/apply-actions`,
        {
          run_id: runId,
          project_id: targetProjectId,
          assigned_to: actionForm.assigned_to ? Number(actionForm.assigned_to) : null,
          board_node_id: actionForm.board_node_id ? Number(actionForm.board_node_id) : null,
          start_date: actionForm.start_date || null,
        }
      );
      window.alert(`${response.data?.created_count || 0} AI aksiyonu göreve dönüştürüldü.`);
      setActionModalOpen(false);
      setActionModalProject(null);
    } catch (applyError) {
      const message = applyError.response?.data?.message || 'AI aksiyonları göreve dönüştürülemedi.';
      setAiError(message);
      setActionModalError(message);
    } finally {
      setApplyingActionProjectId(null);
    }
  };

  const openAiActionModal = async (project) => {
    const projectId = Number(project?.project_id || 0);
    if (!projectId) return;

    setActionModalOpen(true);
    setActionModalProject(project);
    setActionModalUsers([]);
    setActionModalBoards([]);
    setActionModalError('');
    setActionForm({
      assigned_to: '',
      board_node_id: '',
      start_date: new Date().toISOString().slice(0, 10),
    });

    try {
      setActionModalLoading(true);
      const [usersResponse, boardResponse] = await Promise.all([
        apiClient.get(`${API_BASE}/users/options`),
        apiClient.get(`${API_BASE}/tasks/boards/project/${projectId}/tree`),
      ]);

      setActionModalUsers(
        Array.isArray(usersResponse.data)
          ? usersResponse.data.filter((item) => [2, 3, 4, 5].includes(Number(item.role_id)))
          : []
      );
      setActionModalBoards(
        Array.isArray(boardResponse.data?.tree) ? flattenBoardOptions(boardResponse.data.tree) : []
      );
    } catch (requestError) {
      setActionModalError(requestError.response?.data?.message || 'AI görev seçenekleri yüklenemedi.');
    } finally {
      setActionModalLoading(false);
    }
  };

  const closeAiActionModal = () => {
    if (applyingActionProjectId) return;
    setActionModalOpen(false);
    setActionModalProject(null);
    setActionModalUsers([]);
    setActionModalBoards([]);
    setActionModalError('');
  };

  const getProgressPercentage = (project) => {
    if (project.status === 'completed') return 100;
    if (project.status === 'in_progress') return 60;
    if (project.status === 'planning') return 20;
    return 0;
  };

  const deliveryByChannel = useMemo(() => {
    const channels = ['in_app', 'email', 'whatsapp', 'push'];
    const summaryMap = {};
    for (const channel of channels) {
      summaryMap[channel] = { sent: 0, failed: 0, skipped: 0 };
    }

    for (const row of notificationMetrics.delivery_summary) {
      const channel = String(row.channel || '').toLowerCase();
      const status = String(row.status || '').toLowerCase();
      if (!summaryMap[channel] || !Object.prototype.hasOwnProperty.call(summaryMap[channel], status)) {
        continue;
      }
      summaryMap[channel][status] += Number(row.total || 0);
    }

    return summaryMap;
  }, [notificationMetrics.delivery_summary]);

  if (loading) {
    return <div className="dashboard-container loading-text">Yükleniyor...</div>;
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1>Pano</h1>
        <p className="welcome-text">İnşaat Proje Yönetim Sistemine Hoş Geldiniz</p>
      </div>

      {error && <div className="error-alert">{error}</div>}

      <div className="stats-grid">
        <Link to="/projects" className="stat-card stat-link-card" title="Projeler sayfasına git">
          <div className="stat-icon projects" aria-hidden="true">
            {iconMode === 'emoji' ? (
              <span className="dashboard-emoji-icon">{DASHBOARD_EMOJI_ICONS.construction}</span>
            ) : (
              <AppIcon name="construction" className="dashboard-icon-svg" />
            )}
          </div>
          <div className="stat-content">
            <p className="stat-label">Toplam Projeler</p>
            <p className="stat-value">{stats.totalProjects}</p>
          </div>
        </Link>

        <Link to="/projects?status=in_progress" className="stat-card stat-link-card" title="Aktif projeleri göster">
          <div className="stat-icon active" aria-hidden="true">
            {iconMode === 'emoji' ? (
              <span className="dashboard-emoji-icon">{DASHBOARD_EMOJI_ICONS.active}</span>
            ) : (
              <AppIcon name="active" className="dashboard-icon-svg" />
            )}
          </div>
          <div className="stat-content">
            <p className="stat-label">Aktif Projeler</p>
            <p className="stat-value">{stats.activeProjects}</p>
          </div>
        </Link>

        <Link to="/projects?status=completed" className="stat-card stat-link-card" title="Tamamlanan projeleri göster">
          <div className="stat-icon completed" aria-hidden="true">
            {iconMode === 'emoji' ? (
              <span className="dashboard-emoji-icon">{DASHBOARD_EMOJI_ICONS.completed}</span>
            ) : (
              <AppIcon name="completed" className="dashboard-icon-svg" />
            )}
          </div>
          <div className="stat-content">
            <p className="stat-label">Tamamlanan Projeler</p>
            <p className="stat-value">{stats.completedProjects}</p>
          </div>
        </Link>

        <Link to="/tasks?status=in_progress" className="stat-card stat-link-card" title="Aktif görevleri göster">
          <div className="stat-icon tasks" aria-hidden="true">
            {iconMode === 'emoji' ? (
              <span className="dashboard-emoji-icon">{DASHBOARD_EMOJI_ICONS.tasks}</span>
            ) : (
              <AppIcon name="tasks" className="dashboard-icon-svg" />
            )}
          </div>
          <div className="stat-content">
            <p className="stat-label">Aktif Görevler</p>
            <p className="stat-value">{stats.activeTasks}</p>
          </div>
        </Link>
      </div>

      <div className="quick-actions">
        <Link to="/projects" className="action-btn">
          <span className="action-icon" aria-hidden="true">
            {iconMode === 'emoji' ? (
              <span className="action-emoji-icon">{DASHBOARD_EMOJI_ICONS.projects}</span>
            ) : (
              <AppIcon name="projects" className="action-icon-svg" />
            )}
          </span>
          Projeleri G?r?nt?le
        </Link>
        <Link to="/projects" className="action-btn">
          <span className="action-icon" aria-hidden="true">
            {iconMode === 'emoji' ? (
              <span className="action-emoji-icon">{DASHBOARD_EMOJI_ICONS.plus}</span>
            ) : (
              <AppIcon name="plus" className="action-icon-svg" />
            )}
          </span>
          Yeni Proje Oluştur
        </Link>
        <Link to="/tasks" className="action-btn">
          <span className="action-icon" aria-hidden="true">
            {iconMode === 'emoji' ? (
              <span className="action-emoji-icon">{DASHBOARD_EMOJI_ICONS.tasks}</span>
            ) : (
              <AppIcon name="tasks" className="action-icon-svg" />
            )}
          </span>
          Görevleri Yönet
        </Link>
      </div>

      {canViewCapacity && (
        <div className="capacity-section">
          <div className="section-header">
            <h2>Kaynak Kapasite Merkezi</h2>
            <div className="capacity-head-actions">
              <select
                className="capacity-range-select"
                value={capacityWeekDays}
                onChange={(event) => setCapacityWeekDays(Number(event.target.value))}
              >
                 <option value={7}>7 Gün</option>
                 <option value={14}>14 Gün</option>
                 <option value={21}>21 Gün</option>
              </select>
              <Link to="/operations-center" className="view-all">Detaya Git</Link>
            </div>
          </div>

          {capacityError && <div className="error-alert">{capacityError}</div>}

          {capacityLoading ? (
             <div className="metrics-loading">Kapasite verileri yükleniyor...</div>
          ) : (
            <>
              <div className="capacity-summary-grid">
                <div className="capacity-summary-card">
                  <span>Takip Edilen Personel</span>
                  <strong>{capacityOverview.summary.total_people}</strong>
                </div>
                <div className="capacity-summary-card">
                   <span>Aşırı Yüklenme</span>
                  <strong>{capacityOverview.summary.overloaded_people}</strong>
                </div>
                <div className="capacity-summary-card">
                  <span>Kritik Risk</span>
                  <strong>{capacityOverview.summary.critical_people}</strong>
                </div>
                <div className="capacity-summary-card">
                  <span>Ortalama Doluluk</span>
                  <strong>%{capacityOverview.summary.average_utilization_pct}</strong>
                </div>
              </div>

              <details className="dashboard-accordion">
                 <summary>Kritik Uyarılar ({capacityOverview.alerts.length})</summary>
                {capacityOverview.alerts.length > 0 ? (
                  <div className="capacity-alert-list">
                    {capacityOverview.alerts.slice(0, 4).map((alert) => (
                      <div key={`${alert.user_id}-${alert.level}`} className={`capacity-alert-item ${alert.level}`}>
                        <div className="capacity-alert-message">{alert.message}</div>
                        <div className="capacity-alert-suggestion">{alert.suggestion}</div>
                      </div>
                    ))}
                  </div>
                ) : (
                   <p className="capacity-rebalance-empty">Bu zaman aralığında kritik uyarı bulunmuyor.</p>
                )}
              </details>

              <details className="dashboard-accordion">
                <summary>
                   Dengeleme Önerileri ({capacityOverview.rebalancing.summary.total_suggestions})
                </summary>
                {capacityOverview.rebalancing.suggestions.length > 0 ? (
                  <div className="capacity-rebalance-list">
                    {capacityOverview.rebalancing.suggestions.slice(0, 4).map((item, index) => (
                      <div className="capacity-rebalance-item" key={`rebalance-${index}-${item.from_user_id}-${item.to_user_id}`}>
                        <div className="capacity-rebalance-route">
                          <strong>{item.from_username}</strong>
                          <span>-&gt;</span>
                          <strong>{item.to_username}</strong>
                        </div>
                        <div className="capacity-rebalance-meta">
                          <span>Kaynak %{item.projected_from_utilization_pct} | Hedef %{item.projected_to_utilization_pct}</span>
                           <span>{item.suggested_task_title || 'Uygun açık işlerden seçim gerekli.'}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                   <p className="capacity-rebalance-empty">Şu an için yeniden dağıtım önerisi yok.</p>
                )}
              </details>
            </>
          )}
        </div>
      )}

      {canViewEarlyWarning && (
        <div className="early-warning-section">
          <div className="section-header">
             <h2>Yönetici Erken Uyarı Merkezi</h2>
            <Link to="/operations-center" className="view-all">Detaya Git</Link>
          </div>

          {earlyWarningError && <div className="error-alert">{earlyWarningError}</div>}

          {earlyWarningLoading ? (
             <div className="metrics-loading">Erken uyarı verileri yükleniyor...</div>
          ) : (
            <>
              <div className="warning-summary-grid">
                <div className="warning-summary-card">
                   <span>İzlenen Proje</span>
                  <strong>{earlyWarningData.summary.tracked_projects}</strong>
                </div>
                <div className="warning-summary-card">
                  <span>Kritik</span>
                  <strong>{earlyWarningData.summary.critical_projects}</strong>
                </div>
                <div className="warning-summary-card">
                   <span>Yüksek Risk</span>
                  <strong>{earlyWarningData.summary.high_projects}</strong>
                </div>
                <div className="warning-summary-card">
                   <span>Geciken Görev</span>
                  <strong>{earlyWarningData.summary.overdue_task_total}</strong>
                </div>
              </div>

              <details className="dashboard-accordion">
                 <summary>Kritik Proje Uyarıları ({earlyWarningData.alerts.length})</summary>
                {earlyWarningData.alerts.length > 0 ? (
                  <div className="warning-alert-list">
                    {earlyWarningData.alerts.slice(0, 4).map((alert) => (
                      <div key={`warning-alert-${alert.project_id}`} className={`warning-alert-item ${alert.level}`}>
                        <div className="warning-alert-title">{alert.message}</div>
                        {Array.isArray(alert.suggestions) && alert.suggestions.length > 0 && (
                          <div className="warning-alert-suggestions">
                            {alert.suggestions.slice(0, 2).map((suggestion, idx) => (
                              <span key={`warning-suggestion-${alert.project_id}-${idx}`}>{suggestion}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                   <p className="capacity-rebalance-empty">Kritik erken uyarı kaydı bulunmuyor.</p>
                )}
              </details>
            </>
          )}
        </div>
      )}

      <div className="notification-metrics-section">
        <div className="section-header">
          <h2>Bildirim Metrikleri</h2>
          <div className="metrics-header-controls">
            <span className="metrics-scope">
               Kapsam: {notificationMetrics.scope === 'global' ? 'Tüm Sistem' : 'Hesabım'} ({notificationMetrics.range_days} gün)
            </span>
            <select
              className="metrics-range-select"
              value={metricsRangeDays}
              onChange={(event) => setMetricsRangeDays(Number(event.target.value))}
            >
               <option value={7}>Son 7 gün</option>
               <option value={30}>Son 30 gün</option>
               <option value={90}>Son 90 gün</option>
            </select>
            <button
              type="button"
              className="btn-small metrics-export-btn"
              onClick={exportNotificationMetricsCsv}
              disabled={metricsExporting}
            >
               {metricsExporting ? 'Hazırlanıyor...' : 'CSV İndir'}
            </button>
          </div>
        </div>

         {metricsLoading && <div className="metrics-loading">Metrikler yükleniyor...</div>}

        <div className="metrics-summary-grid">
          <div className="metrics-summary-card">
             <div className="metrics-summary-label">Oluşan Bildirim</div>
            <div className="metrics-summary-value">{notificationMetrics.summary.created_count}</div>
          </div>
          <div className="metrics-summary-card">
            <div className="metrics-summary-label">Okunan Bildirim</div>
            <div className="metrics-summary-value">{notificationMetrics.summary.read_count}</div>
          </div>
          <div className="metrics-summary-card">
             <div className="metrics-summary-label">Okunmamış Toplam</div>
            <div className="metrics-summary-value">{notificationMetrics.summary.unread_count}</div>
          </div>
        </div>

        <div className="metrics-channel-grid">
          {Object.keys(deliveryByChannel).map((channelKey) => (
            <div className="metrics-channel-card" key={`channel-${channelKey}`}>
              <div className="metrics-channel-title">{channelKey.toUpperCase()}</div>
              <div className="metrics-channel-row">
                 <span>Gönderildi</span>
                <strong>{deliveryByChannel[channelKey].sent}</strong>
              </div>
              <div className="metrics-channel-row">
                 <span>Başarısız</span>
                <strong>{deliveryByChannel[channelKey].failed}</strong>
              </div>
              <div className="metrics-channel-row">
                 <span>Atland?</span>
                <strong>{deliveryByChannel[channelKey].skipped}</strong>
              </div>
            </div>
          ))}
        </div>

        {notificationMetrics.type_breakdown.length > 0 && (
          <div className="metrics-type-list">
             <strong>Tür Dağılımı:</strong>
            {notificationMetrics.type_breakdown.map((row) => (
              <span key={`type-${row.type}`} className="metrics-type-chip">
                {row.type}: {row.total}
              </span>
            ))}
          </div>
        )}

        {notificationMetrics.recent_failures.length > 0 && (
          <div className="metrics-failure-list">
             <strong>Son Başarısız Gönderimler</strong>
            {notificationMetrics.recent_failures.map((row, index) => (
              <div className="metrics-failure-item" key={`failure-${index}-${row.created_at}`}>
                <span>{row.channel}</span>
                 <span>{row.username || `Kullanıcı#${row.user_id}`}</span>
                <span>{row.error_message || '-'}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {canUseAiScan && (
        <div className="ai-health-section">
          <div className="section-header">
             <h2>AI Proje Sağlık Skoru</h2>
            <button type="button" className="btn-small" onClick={fetchAiHealthOverview} disabled={aiHealthLoading}>
               {aiHealthLoading ? 'Güncelleniyor...' : 'Skoru Yenile'}
            </button>
          </div>

          {aiHealthError && <div className="error-alert">{aiHealthError}</div>}

          <div className="ai-health-summary-grid">
            <div className="summary-item">
               <span className="summary-label">Portföy Sağlık Skoru</span>
              <strong className="summary-value">{aiHealthOverview.summary.portfolio_health_score}</strong>
            </div>
            <div className="summary-item">
               <span className="summary-label">?zlenen Proje</span>
              <strong className="summary-value">{aiHealthOverview.summary.project_count}</strong>
            </div>
            <div className="summary-item">
              <span className="summary-label">Kritik Proje</span>
              <strong className="summary-value">{aiHealthOverview.summary.critical_projects}</strong>
            </div>
            <div className="summary-item">
              <span className="summary-label">Sağlıklı Proje</span>
              <strong className="summary-value">{aiHealthOverview.summary.healthy_projects}</strong>
            </div>
            <div className="summary-item">
              <span className="summary-label">Maliyet Baskisi</span>
              <strong className="summary-value">{aiHealthOverview.cost_context?.pressure_level || '-'}</strong>
            </div>
          </div>

          {aiHealthOverview.rows.length > 0 && (
            <div className="ai-health-list">
              {aiHealthOverview.rows.map((project) => (
                <div key={`ai-health-${project.project_id}`} className="ai-health-item">
                  <div className="ai-health-top">
                    <div>
                      <strong>{project.project_name}</strong>
                      <div className="ai-health-meta">
                        <span>Skor: {project.health_score}</span>
                        <span>Risk: {project.risk_level}</span>
                      </div>
                    </div>
                    <Link to={`/projects/${project.project_id}`} className="btn-small">
                      Proje Detayı
                    </Link>
                  </div>
                  <div className="ai-health-breakdown">
                    <span>Termin {project.score_breakdown?.schedule_score ?? '-'}</span>
                    <span>İşgücü {project.score_breakdown?.workforce_score ?? '-'}</span>
                    <span>Dokümantasyon {project.score_breakdown?.documentation_score ?? '-'}</span>
                    <span>Kalite {project.score_breakdown?.quality_score ?? '-'}</span>
                    <span>Risk {project.score_breakdown?.risk_score ?? '-'}</span>
                    <span>Maliyet {project.score_breakdown?.cost_score ?? '-'}</span>
                  </div>
                  <div className="ai-health-reasons">
                    {(project.reasons || []).slice(0, 2).map((reason) => (
                      <span key={`${project.project_id}-health-${reason}`}>{reason}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {canUseAiScan && (
        <div className="ai-health-section">
          <div className="section-header">
            <h2>AI Risk Tahmini</h2>
            <button type="button" className="btn-small" onClick={fetchRiskForecast} disabled={riskForecastLoading}>
              {riskForecastLoading ? 'Hesaplanıyor...' : 'Tahmini Yenile'}
            </button>
          </div>

          {riskForecastError && <div className="error-alert">{riskForecastError}</div>}

          <div className="ai-health-summary-grid">
            <div className="summary-item">
              <span className="summary-label">İzlenen Proje</span>
              <strong className="summary-value">{riskForecastData.summary.tracked_projects}</strong>
            </div>
            <div className="summary-item">
              <span className="summary-label">Kritik Tahmin</span>
              <strong className="summary-value">{riskForecastData.summary.critical_projects}</strong>
            </div>
            <div className="summary-item">
              <span className="summary-label">Yüksek Risk</span>
              <strong className="summary-value">{riskForecastData.summary.high_risk_projects}</strong>
            </div>
            <div className="summary-item">
              <span className="summary-label">Ortalama Olasilik</span>
              <strong className="summary-value">%{riskForecastData.summary.average_delay_probability_pct}</strong>
            </div>
          </div>

          {riskForecastData.rows.length > 0 && (
            <div className="ai-health-list">
              {riskForecastData.rows.map((item) => (
                <div key={`risk-forecast-${item.project_id}`} className="ai-health-item">
                  <div className="ai-health-top">
                    <div>
                      <strong>{item.project_name}</strong>
                      <div className="ai-health-meta">
                        <span>7 gün içinde gecikme: %{item.delay_probability_pct}</span>
                        <span>Seviye: {item.forecast_level}</span>
                        <span>Sağlık: {item.health_score}</span>
                      </div>
                    </div>
                    <Link to={`/projects/${item.project_id}`} className="btn-small">
                      Proje Detayı
                    </Link>
                  </div>
                  <div className="ai-health-reasons">
                    {(item.key_drivers || []).map((reason) => (
                      <span key={`forecast-driver-${item.project_id}-${reason}`}>{reason}</span>
                    ))}
                  </div>
                  <div className="ai-health-breakdown">
                    {(item.recommended_actions || []).map((action) => (
                      <span key={`forecast-action-${item.project_id}-${action}`}>{action}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {canUseAiScan && (
        <div className="ai-scan-section">
          <div className="section-header">
            <h2>AI Proje Tarama</h2>
            <button
              type="button"
              className="btn-small"
              onClick={() => runAiScan()}
              disabled={aiLoading}
            >
              {aiLoading ? 'Analiz Yapılıyor...' : 'AI Analiz Başlat'}
            </button>
          </div>

          {aiError && <div className="error-alert">{aiError}</div>}

          {aiResult && (
            <div className="ai-result-grid">
              <div className="ai-result-summary">
                <div><strong>Tarama Zamanı:</strong> {new Date(aiResult.scanned_at).toLocaleString('tr-TR')}</div>
                <div><strong>Proje Sayısı:</strong> {aiResult.project_count}</div>
                <div><strong>Portföy Sağlık Skoru:</strong> {aiResult.portfolio_health_score}</div>
              </div>

              {(aiResult.projects || []).map((project) => (
                <div key={`ai-${project.project_id}`} className="ai-project-card">
                  <div className="ai-project-head">
                    <h3>{project.project_name}</h3>
                    <span className={`ai-risk ${project.risk_level}`}>{project.risk_level}</span>
                  </div>
                  <div className="ai-project-meta">
                    <span>Sağlık Skoru: {project.health_score}</span>
                    <span>Aşama: {project.inferred_phase_key}</span>
                    <span>Disiplin: {project.inferred_discipline_key}</span>
                  </div>
                  <div className="ai-block">
                    <strong>Nedenler</strong>
                    <ul>
                      {(project.reasons || []).map((reason) => (
                        <li key={`${project.project_id}-reason-${reason}`}>{reason}</li>
                      ))}
                    </ul>
                  </div>
                  <div className="ai-block">
                    <strong>Bu Hafta Önerilen 5 Aksiyon</strong>
                    <ul>
                      {(project.weekly_actions || []).map((action) => (
                        <li key={`${project.project_id}-action-${action}`}>{action}</li>
                      ))}
                    </ul>
                  </div>
                  <div className="ai-actions-row">
                    <button
                      type="button"
                      className="btn-small"
                      onClick={() => applyAiChecklist(project.project_id)}
                      disabled={applyingProjectId === Number(project.project_id)}
                    >
                      {applyingProjectId === Number(project.project_id) ? 'Aktarılıyor...' : "Checklist'e Aktar"}
                    </button>
                    <button
                      type="button"
                      className="btn-small"
                      onClick={() => openAiActionModal(project)}
                      disabled={applyingActionProjectId === Number(project.project_id)}
                    >
                      {applyingActionProjectId === Number(project.project_id) ? 'Oluşturuluyor...' : 'Göreve Dönüştür'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {aiHistory.length > 0 && (
            <div className="ai-history-section">
              <h3>AI Geçmiş Analizler</h3>
              <div className="ai-history-list">
                {aiHistory.map((run) => (
                  <div key={`run-${run.id}`} className="ai-history-item">
                    <span>#{run.id}</span>
                    <span>{new Date(run.created_at).toLocaleString('tr-TR')}</span>
                    <span>Skor: {run.portfolio_health_score ?? '-'}</span>
                    <span>Proje: {run.project_count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {actionModalOpen && actionModalProject && (
        <div className="modal-overlay">
          <div className="modal-content ai-action-modal">
            <div className="modal-header">
              <h3 className="modal-title">AI Aksiyonlarini Göreve Dönüştür</h3>
              <button type="button" className="btn-close" onClick={closeAiActionModal}>x</button>
            </div>

            <div className="ai-action-helper">
              <strong>{actionModalProject.project_name}</strong>
              <span>Haftalık AI aksiyonları seçilen parametrelerle görev kartlarına dönüştürülecek.</span>
            </div>

            {actionModalError && <div className="error-alert">{actionModalError}</div>}

            <div className="ai-action-form-grid">
              <label className="form-group">
                <span>Sorumlu Kişi</span>
                <select
                  className="form-control"
                  value={actionForm.assigned_to}
                  onChange={(event) => setActionForm((prev) => ({ ...prev, assigned_to: event.target.value }))}
                  disabled={actionModalLoading || Boolean(applyingActionProjectId)}
                >
                  <option value="">Atama yapma</option>
                  {actionModalUsers.map((item) => (
                    <option key={`action-user-${item.id}`} value={item.id}>
                      {item.username}{item.job_title ? ` (${item.job_title})` : ''}
                    </option>
                  ))}
                </select>
              </label>

              <label className="form-group">
                <span>Pano Katmanı</span>
                <select
                  className="form-control"
                  value={actionForm.board_node_id}
                  onChange={(event) => setActionForm((prev) => ({ ...prev, board_node_id: event.target.value }))}
                  disabled={actionModalLoading || Boolean(applyingActionProjectId)}
                >
                  <option value="">Genel görev havuzu</option>
                  {actionModalBoards.map((item) => (
                    <option key={`action-board-${item.id}`} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </select>
              </label>

              <label className="form-group">
                <span>Başlangıç Tarihi</span>
                <input
                  type="date"
                  className="form-control"
                  value={actionForm.start_date}
                  onChange={(event) => setActionForm((prev) => ({ ...prev, start_date: event.target.value }))}
                  disabled={actionModalLoading || Boolean(applyingActionProjectId)}
                />
              </label>
            </div>

            <div className="form-actions ai-action-modal-actions">
              <button type="button" className="secondary-btn" onClick={closeAiActionModal}>
                Vazgeç
              </button>
              <button
                type="button"
                className="btn-small"
                onClick={() => applyAiActions(actionModalProject.project_id)}
                disabled={actionModalLoading || applyingActionProjectId === Number(actionModalProject.project_id)}
              >
                {applyingActionProjectId === Number(actionModalProject.project_id) ? 'Oluşturuluyor...' : 'Görevleri Oluştur'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="recent-section">
        <div className="section-header">
          <h2>Son Projeler</h2>
          <Link to="/projects" className="view-all">Tümünü Görüntüle</Link>
        </div>

        {recentProjects.length === 0 ? (
          <div className="empty-section">
            <p>Henüz proje yok.</p>
            <Link to="/projects" className="btn-primary-link">Proje Oluştur</Link>
          </div>
        ) : (
          <div className="recent-projects-list">
            {recentProjects.map((project) => (
              <div key={project.id} className="recent-project-item">
                <div className="project-info">
                  <h3>
                    <Link to={`/projects/${project.id}`}>{project.name}</Link>
                  </h3>
                  <p className="project-location">{project.location || 'Konum belirtilmedi'}</p>
                  <p className="project-dates">
                    {project.start_date ? new Date(project.start_date).toLocaleDateString('tr-TR') : '-'}
                    {' - '}
                    {project.end_date ? new Date(project.end_date).toLocaleDateString('tr-TR') : '-'}
                  </p>
                </div>

                <div className="project-progress">
                  <div className="progress-bar">
                    <div
                      className="progress-fill"
                      style={{ width: `${getProgressPercentage(project)}%` }}
                    />
                  </div>
                  <p className="status-text">{STATUS_LABELS[project.status] || project.status}</p>
                </div>

                <Link to={`/projects/${project.id}`} className="btn-small">
                  Detaylar
                </Link>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="summary-section">
        <h2>Hızlı İstatistikler</h2>
        <div className="summary-grid">
          <div className="summary-item">
            <span className="summary-label">Ortalama Proje Durumu</span>
            <span className="summary-value">
              {stats.totalProjects > 0 ? ((stats.activeProjects / stats.totalProjects) * 100).toFixed(0) : 0}% Devam
            </span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Tamamlanma Oranı</span>
            <span className="summary-value">
              {stats.totalProjects > 0 ? ((stats.completedProjects / stats.totalProjects) * 100).toFixed(0) : 0}%
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;

